/**
 * Frontend Debug Utilities - Centralized Event Tracking
 * Mirrors the backend debug system for comprehensive event lifecycle tracking
 */

// Debug configuration - set event names to trace
// Can be updated dynamically or via URL parameters
export let DEBUG_EVENT_NAMES = [];
export const VERBOSE_FRONTEND_LOGGING = false;

// Function to update debug event names dynamically
export const setDebugEvents = (eventNames) => {
  DEBUG_EVENT_NAMES = Array.isArray(eventNames) ? eventNames : [eventNames];
};

// Debug helper function with organized formatting
export const createDebugLogger = (module) => {
  const log = (message, data = {}) => {
    if (!VERBOSE_FRONTEND_LOGGING) return;
    console.log(`[${module}] ${message}`, data);
  };

  const eventTrace = (stage, event, extraData = {}) => {
    if (!event || !DEBUG_EVENT_NAMES.includes(event.name)) return;
    
    console.log(`[${module}] `);
    console.log(`============================================================`);
    console.log(`[${module}] 🔍 EVENT TRACKING - ${stage.toUpperCase()}`);
    console.log(`============================================================`);
    console.log(`[${module}] 📍 Event: "${event.name}" (ID: ${event._id || event.id})`);
    console.log(`[${module}]    Type: ${event.type}`);
    console.log(`[${module}]    Venue: ${event.venue || event.venueDetails?._id}`);
    
    if (event.nextOccurence) {
      console.log(`[${module}]    Next: ${new Date(event.nextOccurence.start)} - ${new Date(event.nextOccurence.end)}`);
    }
    if (event.date) {
      console.log(`[${module}]    Original: ${new Date(event.date.start)} - ${new Date(event.date.end)}`);
    }
    if (event.timezone) {
      console.log(`[${module}]    Timezone: ${JSON.stringify(event.timezone)}`);
    }
    
    // Display extra data
    Object.keys(extraData).forEach(key => {
      console.log(`[${module}]    ${key}: ${JSON.stringify(extraData[key])}`);
    });
    
    console.log(`[${module}]    Status: ✅ PRESENT`);
    console.log(`============================================================`);
    console.log(`[${module}] `);
  };

  const dateTrace = (functionName, event, input, output, calculation = {}) => {
    if (!event || !DEBUG_EVENT_NAMES.includes(event.name)) return;
    
    console.log(`[${module}] `);
    console.log(`============================================================`);
    console.log(`[${module}] 📅 DATE CALCULATION - ${functionName.toUpperCase()}`);
    console.log(`============================================================`);
    console.log(`[${module}] 📍 Event: "${event.name}"`);
    console.log(`[${module}]    Input Date: ${JSON.stringify(input)}`);
    console.log(`[${module}]    Timezone: ${JSON.stringify(event.timezone)}`);
    console.log(`[${module}]    Calculation Details:`, calculation);
    console.log(`[${module}]    Output: "${output}"`);
    console.log(`============================================================`);
    console.log(`[${module}] `);
  };

  const conversionTrace = (stage, event, beforeData, afterData) => {
    if (!event || !DEBUG_EVENT_NAMES.includes(event.name)) return;
    
    console.log(`[${module}] [trace:${stage}] Event: "${event.name}"`);
    console.log(`[${module}] [trace:${stage}:before]`, beforeData);
    console.log(`[${module}] [trace:${stage}:after]`, afterData);
  };

  return { log, eventTrace, dateTrace, conversionTrace };
};

// Event identification helper
export const isDebugEvent = (event) => {
  return event && DEBUG_EVENT_NAMES.includes(event.name);
};

// Data transformation tracker
export const trackTransformation = (module, stage, events) => {
  if (!VERBOSE_FRONTEND_LOGGING) return;
  
  const debugEvents = events?.filter?.(isDebugEvent) || (isDebugEvent(events) ? [events] : []);
  
  if (debugEvents.length > 0) {
    const logger = createDebugLogger(module);
    debugEvents.forEach(event => {
      logger.eventTrace(stage, event);
    });
  }
};

// Date calculation tracker
export const trackDateCalculation = (module, functionName, event, input, output, calculation = {}) => {
  if (!VERBOSE_FRONTEND_LOGGING || !isDebugEvent(event)) return;
  
  const logger = createDebugLogger(module);
  logger.dateTrace(functionName, event, input, output, calculation);
};
